import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip } from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, zhCN } from '@mui/x-data-grid';
import { useVisibilityChange } from '@uidotdev/usehooks';
import { useSearchParams } from 'react-router-dom';

import { callApi } from 'utils';

const PAGE_SIZE = 100;
const PRIMARY_TEXT_COLUMN = {
  minWidth: 260,
  flex: 4,
  align: 'left',
  headerAlign: 'left',
};
const COLUMN_PRESETS = {
  primaryText: PRIMARY_TEXT_COLUMN,
};
const dataGridLocaleText = zhCN.components.MuiDataGrid.defaultProps.localeText;

const ColumnSettingsToolbar = () => (
  <GridToolbarContainer sx={{justifyContent: 'flex-end', px: 1, py: 0.5}}>
    <GridToolbarColumnsButton />
  </GridToolbarContainer>
);

const AppDataGridColumnSettingsButton = React.forwardRef(function AppDataGridColumnSettingsButton({
  apiRef,
  buttonId,
  panelId,
  label,
  onClick,
  ...props
}, ref) {
  const handleClick = useCallback((event) => {
    const api = apiRef?.current;
    if (!api?.showPreferences) return;

    const preferencePanel = api.state?.preferencePanel;
    if (preferencePanel?.open && preferencePanel.openedPanelValue === 'columns') {
      api.hidePreferences();
    } else {
      api.showPreferences('columns', panelId, buttonId);
    }

    onClick?.(event);
  }, [apiRef, buttonId, onClick, panelId]);

  return (
    <Tooltip title={label}>
      <IconButton
        {...props}
        aria-label={label}
        aria-haspopup="menu"
        color="inherit"
        id={buttonId}
        onClick={handleClick}
        ref={ref}
        size="small"
      >
        <ViewColumnIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
});

AppDataGridColumnSettingsButton.propTypes = {
  apiRef: PropTypes.object.isRequired,
  buttonId: PropTypes.string.isRequired,
  panelId: PropTypes.string.isRequired,
  label: PropTypes.string,
  onClick: PropTypes.func,
};

AppDataGridColumnSettingsButton.defaultProps = {
  label: dataGridLocaleText.toolbarColumns || 'Columns',
};

const getTogglableColumns = (columns) => (
  columns
    .filter(column => !String(column.field).startsWith('__'))
    .map(column => column.field)
);

function buildPageUrl(dataUrl, page) {
  if (!dataUrl) return '';

  const usp = new URLSearchParams({page, limit: PAGE_SIZE});
  return dataUrl + (dataUrl.includes('?') ? '&' : '?') + usp.toString();
}

function normalizeData(data) {
  if (Array.isArray(data)) {
    return {rows: data, total: data.length};
  }

  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    total: Number(data?.total) || 0,
  };
}

function loopLoadData(url, cb, setLoading, setSettledPageUrl, refresh, active) {
  if (!active || !url) return;

  let canceled = false;
  const loadData = () => {
    setLoading(true);
    return callApi(url).then((res) => {
      if (!canceled) cb(normalizeData(res));
    }).catch(console.error).finally(() => {
      if (!canceled) {
        setSettledPageUrl(url);
        setLoading(false);
      }
    });
  };

  loadData();

  if (refresh <= 0) {
    return () => { canceled = true; };
  }

  const itvl = setInterval(() => loadData(), refresh * 1000);
  return () => {
    canceled = true;
    clearInterval(itvl);
  };
}

const AppDataGrid = ({
  columns,
  dataUrl,
  dataAmend,
  idColumn,
  onDataChange,
  selected,
  onSelectedChange,
  isRowSelectable,
  refresh,
  updatePageUrl,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  showColumnSettings,
  showColumnSettingsToolbar,
  slots,
  slotProps,
  localeText,
  sx,
  ...others
}) => {
  const documentVisible = useVisibilityChange();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({rows: [], total: 0});
  const [loading, setLoading] = useState(false);
  const [settledPageUrl, setSettledPageUrl] = useState('');
  const [localPage, setLocalPage] = useState(0);

  const page = updatePageUrl ? Math.max(Number(searchParams.get('page')) || 0, 0) : localPage;
  const pageUrl = useMemo(() => buildPageUrl(dataUrl, page), [dataUrl, page]);

  let active = documentVisible;
  if (refresh <= 0)
    active = true;

  const multiSelect = Array.isArray(selected);

  const gridLoading = loading || Boolean(pageUrl && settledPageUrl !== pageUrl);

  useEffect(() => loopLoadData(pageUrl, setData, setLoading, setSettledPageUrl, refresh, active), [pageUrl, refresh, active]);
  useEffect(() => void(onDataChange?.(data)), [data, onDataChange]);
  useEffect(() => void(multiSelect && onSelectedChange?.([])), [dataUrl, page, multiSelect, onSelectedChange]);
  useEffect(() => void(updatePageUrl || setLocalPage(0)), [updatePageUrl, dataUrl]);
  useEffect(() => {
    if (!updatePageUrl || !searchParams.has('limit')) return;
    setSearchParams((prev) => {
      prev.delete('limit');
      return prev;
    });
  }, [searchParams, setSearchParams, updatePageUrl]);

  const rows = useMemo(() => {
    const sourceRows = data.rows || [];
    if (!dataAmend) return sourceRows;

    return sourceRows.map(row => ({
      ...row,
      ...dataAmend[row[idColumn]],
    }));
  }, [data.rows, dataAmend, idColumn]);

  const paginationModel = useMemo(() => ({
    page,
    pageSize: PAGE_SIZE,
  }), [page]);
  const gridSlots = useMemo(() => (
    showColumnSettings
      ? { ...slots, ...(showColumnSettingsToolbar ? { toolbar: slots?.toolbar || ColumnSettingsToolbar } : {}) }
      : slots
  ), [showColumnSettings, showColumnSettingsToolbar, slots]);
  const gridSlotProps = useMemo(() => (
    showColumnSettings
      ? {
        ...slotProps,
        columnsPanel: {
          getTogglableColumns,
          ...slotProps?.columnsPanel,
        },
      }
      : slotProps
  ), [showColumnSettings, slotProps]);
  const gridLocaleText = useMemo(() => ({
    ...dataGridLocaleText,
    ...localeText,
  }), [localeText]);

  const handlePaginationModelChange = useCallback((model) => {
    const nextPage = model.page || 0;
    if (nextPage === page) return;

    if (updatePageUrl) {
      setSearchParams((prev) => {
        nextPage ? prev.set('page', nextPage) : prev.delete('page');
        prev.delete('limit');
        return prev;
      });
    } else {
      setLocalPage(nextPage);
    }
  }, [page, setSearchParams, updatePageUrl]);

  return (
    <Box sx={{width: '100%'}}>
      <DataGrid
        autoHeight
        checkboxSelection={multiSelect}
        checkboxSelectionVisibleOnly
        columnVisibilityModel={columnVisibilityModel}
        columns={columns}
        density="compact"
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={(row) => row[idColumn]}
        isRowSelectable={(params) => isRowSelectable(params.row)}
        loading={gridLoading}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPaginationModelChange={handlePaginationModelChange}
        onRowSelectionModelChange={multiSelect ? onSelectedChange : undefined}
        pageSizeOptions={[PAGE_SIZE]}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={data.total}
        rows={rows}
        rowSelectionModel={multiSelect ? selected : undefined}
        slots={gridSlots}
        slotProps={gridSlotProps}
        localeText={gridLocaleText}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1.2,
          },
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
            whiteSpace: 'normal',
            lineHeight: 1.25,
          },
          '& .MuiDataGrid-cellContent': {
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'normal',
          },
          '& .AppDataGrid-nowrapCell': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          },
          '& .AppDataGrid-nowrapCell .MuiDataGrid-cellContent': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .AppDataGrid-actionCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
          },
          '& .AppDataGrid-actionCell > .MuiButtonBase-root': {
            flex: '0 0 auto',
          },
          ...sx,
        }}
        {...others}
      />
    </Box>
  );
};

AppDataGrid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  dataUrl: PropTypes.string.isRequired,
  dataAmend: PropTypes.object,
  idColumn: PropTypes.string,
  onDataChange: PropTypes.func,
  selected: PropTypes.array,
  onSelectedChange: PropTypes.func,
  isRowSelectable: PropTypes.func,
  refresh: PropTypes.number,
  updatePageUrl: PropTypes.bool,
  columnVisibilityModel: PropTypes.object,
  onColumnVisibilityModelChange: PropTypes.func,
  showColumnSettings: PropTypes.bool,
  showColumnSettingsToolbar: PropTypes.bool,
  slots: PropTypes.object,
  slotProps: PropTypes.object,
  localeText: PropTypes.object,
  sx: PropTypes.object,
};

AppDataGrid.defaultProps = {
  dataAmend: null,
  idColumn: 'id',
  refresh: 0,
  updatePageUrl: true,
  isRowSelectable: () => true,
  showColumnSettings: false,
  showColumnSettingsToolbar: true,
};

export {
  PAGE_SIZE as APP_DATA_GRID_PAGE_SIZE,
  COLUMN_PRESETS as APP_DATA_GRID_COLUMN_PRESETS,
  AppDataGridColumnSettingsButton,
};
export default AppDataGrid;
